import { Checkbox } from "components/forms/fragments/Checkbox"
import { GenericMessage } from "components/feedback/Message"
import { useDynamicTranslation } from "hooks/useDynamicTranslation"
import { NsKey } from "enums/NsKey"
import { PricingSideMenuCard } from "features/subscription-process/pricing-process/components/common/PricingSideMenuCard"
import { useMainBorrowerPricingActions, useMainBorrowerPricingState } from "../../contexts/main-borrower-pricing/MainBorrowerPricingContext"
import { useActiveFiltersCount, useFilteredContractFunnels } from "../../contexts/main-borrower-pricing/selectors/selectors"
import { SideMenuFilter, SideMenuFilterItem } from "../../contexts/main-borrower-pricing/types"

// Types de filtres à masquer dans l'affichage
const HIDDEN_FILTER_TYPES = ['SUPPORT_TYPE'] as const;

export const BorrowerFunnelFilters = () => {
  const { filters } = useMainBorrowerPricingState()
  const { toggleFilterItem, setAllFilterItems, resetFilters } = useMainBorrowerPricingActions()
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Pricing])

  const filteredContractFunnels = useFilteredContractFunnels()
  const activeFiltersCount = useActiveFiltersCount()

  const handleResetFilters = () => {
    resetFilters()
  }

  const handleToggleFilter = (filterType: string, itemId: string) => {
    toggleFilterItem({ filterId: filterType, itemId })
  }

  const areAllSelected = (filterItems: SideMenuFilterItem[]) => {
    return filterItems.length > 0 && filterItems.every(item => item.value)
  }

  const areNoneSelected = (filterItems: SideMenuFilterItem[]) => {
    return filterItems.every(item => !item.value)
  }

  const handleCategorySelectAll = (filterType: string, filterItems: SideMenuFilterItem[]) => {
    const allSelected = areAllSelected(filterItems)
    setAllFilterItems({ filterId: filterType, value: !allSelected })
  }

  const renderFilterItem = (filterGroup: SideMenuFilter) => {
    const { type, title, items: filterItems } = filterGroup

    // Masquer certains types de filtres
    if (HIDDEN_FILTER_TYPES.includes(type as typeof HIDDEN_FILTER_TYPES[number])) {
      return null
    }

    const allSelected = areAllSelected(filterItems)
    const noneSelected = areNoneSelected(filterItems)
    const someSelected = !allSelected && !noneSelected

    return (
      <div className="bg-[#F2F9FA] flex flex-col gap-2 rounded-xl p-4" key={type}>
        <div className="flex justify-normal gap-2 xl:justify-between mb-1">
          <p className="font-bold text-sm">{title}</p>

          <div className="flex items-center">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => handleCategorySelectAll(type, filterItems)}
            />
          </div>
        </div>

        <div className="flex flex-row justify-between xl:justify-normal xl:flex-col gap-2">
          {filterItems.map((filter) => (
            <div key={filter.id} className="flex justify-between">
              <div className="grow pr-2">
                <p className="font-14 lh-19 font-w400 text-dark">
                  {filter.label}
                </p>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={filter.value}
                  onChange={() => handleToggleFilter(type, filter.id)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <PricingSideMenuCard
      title={t("PRICING.SIDE_MENU.FILTERS")}
      icon="filter-price"
    >
      <div className="relative">
        <div className="flex flex-col gap-4">
          {filters.length === 0 ? (
            <div className="text-center p-4">
              <p className="font-14 text-secondary">{t("PRICING.SIDE_MENU.NO_FILTERS_AVAILABLE")}</p>
            </div>
          ) : (
            filters.map((filterGroup) => renderFilterItem(filterGroup))
          )}
        </div>

        {filteredContractFunnels.length === 0 && activeFiltersCount > 0 && (
          <GenericMessage
            className="mt-4"
            size="sm"
            message={t("PRICING.SIDE_MENU.NO_SELECTED_FILTERS_FOR_FUNNEL")}
            dismissible={false}
            actionButton={{
              label: t("COMMON.REINITIALISE"),
              onClick: handleResetFilters
            }}
            type="warning"
          />
        )}
      </div>
    </PricingSideMenuCard>
  )
}
